/**
 * ReportDialog.jsx
 *
 * MUI Dialog for submitting user reports. Handles:
 *   - Severity selection (info/bug/blocker)
 *   - Area selection (auto-detected from route, user can override)
 *   - Title + description text fields
 *   - Optional screenshot capture via html2canvas (requires consent)
 *   - GDPR consent checkbox
 *
 * Props:
 *   open        {boolean}
 *   onClose     {() => void}
 *   onSuccess   {() => void} — called after successful submission
 *   preSnapshot {Blob|null}  — blob captured before dialog open (from ReportFab)
 *   defaultArea {string}     — auto-detected area
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  Paper,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Typography,
  Alert,
  useMediaQuery,
  Chip,
  Tooltip,
} from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SendIcon from '@mui/icons-material/Send';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined';
import { submitReport } from '../../services/reportService';
import { useReportContext } from '../../hooks/useReportContext';

const SEVERITIES = [
  { value: 'info', label: 'Sugerencia', color: 'info', icon: <EmojiObjectsOutlinedIcon fontSize="small" /> },
  { value: 'bug', label: 'Algo no funciona', color: 'warning', icon: <ReportProblemOutlinedIcon fontSize="small" /> },
  { value: 'blocker', label: 'Estoy bloqueado', color: 'error', icon: <ErrorOutlineIcon fontSize="small" /> },
];

const AREAS = [
  { value: 'chat', label: 'Chat' },
  { value: 'rag', label: 'RAG / Conocimiento' },
  { value: 'builder', label: 'Constructor de Agentes' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'admin', label: 'Admin' },
  { value: 'other', label: 'Otro' },
];

/**
 * Draggable Paper for the dialog. Lets the user move the window aside to read
 * what's behind it while reporting (#83 / report d903e186). Drag starts only
 * from the title bar (marked with data-drag-handle); pointer capture keeps the
 * drag smooth even if the cursor leaves the Paper. No external dep.
 */
const DraggablePaper = (props) => {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const handlePointerDown = useCallback((e) => {
    if (!e.target.closest('[data-drag-handle]')) return;
    drag.current = { startX: e.clientX, startY: e.clientY, ox: pos.x, oy: pos.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [pos.x, pos.y]);

  const handlePointerMove = useCallback((e) => {
    if (!drag.current) return;
    setPos({
      x: drag.current.ox + (e.clientX - drag.current.startX),
      y: drag.current.oy + (e.clientY - drag.current.startY),
    });
  }, []);

  const handlePointerUp = useCallback((e) => {
    if (!drag.current) return;
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  return (
    <Paper
      {...props}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      sx={{ ...props.sx, transform: `translate(${pos.x}px, ${pos.y}px)` }}
    />
  );
};

const ReportDialog = ({ open, onClose, onSuccess, preSnapshot = null, defaultArea = 'other' }) => {
  const isMobile = useMediaQuery('(max-width: 599px)');
  const { build } = useReportContext();

  const [severity, setSeverity] = useState('bug');
  const [area, setArea] = useState(defaultArea);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [screenshotBlob, setScreenshotBlob] = useState(preSnapshot);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Sync preSnapshot when it changes (e.g. dialog opens)
  useEffect(() => {
    setScreenshotBlob(preSnapshot);
  }, [preSnapshot]);

  // Reset state on close
  const handleClose = () => {
    setSeverity('bug');
    setArea(defaultArea);
    setTitle('');
    setDescription('');
    setConsentGiven(false);
    setIncludeScreenshot(true);
    setScreenshotBlob(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);

    if (!title.trim()) {
      setError('El título es obligatorio.');
      return;
    }
    if (title.trim().length > 200) {
      setError('El título debe tener 200 caracteres o menos.');
      return;
    }
    if (description.length > 5000) {
      setError('La descripción debe tener 5000 caracteres o menos.');
      return;
    }

    setSubmitting(true);
    try {
      // Capture screenshot if user wants it and hasn't pre-captured
      let blob = screenshotBlob;
      if (includeScreenshot && consentGiven && !blob) {
        try {
          const html2canvas = (await import('html2canvas')).default;
          const canvas = await html2canvas(document.body, {
            useCORS: true,
            allowTaint: true,
            scale: 0.75,
          });
          blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        } catch (captureErr) {
          console.warn('[ReportDialog] Screenshot capture failed:', captureErr.message);
          blob = null;
        }
      }

      const context = build();

      await submitReport({
        severity,
        area,
        title: title.trim(),
        description: description.trim() || null,
        context,
        consentGiven,
        screenshotBlob: consentGiven && includeScreenshot ? blob : null,
      });

      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      setError(err.message || 'No hemos podido enviar tu feedback. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const charCount = description.length;
  const isOverLimit = charCount > 5000;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      data-testid="report-dialog"
      aria-labelledby="report-dialog-title"
      // Draggable + dimmed backdrop so the user can move the window aside and
      // still see the screen behind while reporting (#83 / d903e186). Skip on
      // mobile, where the dialog is fullscreen and dragging makes no sense.
      PaperComponent={isMobile ? undefined : DraggablePaper}
      slotProps={isMobile ? undefined : { backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.2)' } } }}
    >
      <DialogTitle
        id="report-dialog-title"
        data-drag-handle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 0.5,
          cursor: isMobile ? 'default' : 'move',
          userSelect: 'none',
        }}
      >
        <LightbulbOutlinedIcon sx={{ color: '#F59E0B' }} />
        Tu feedback
        {!isMobile && (
          <Tooltip title="Arrastra para mover la ventana">
            <DragIndicatorIcon
              sx={{ ml: 'auto', color: 'text.disabled', cursor: 'move' }}
            />
          </Tooltip>
        )}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cuéntanos qué pasa o qué echas en falta.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Severity */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
              ¿De qué se trata?
            </Typography>
            {SEVERITIES.map(s => (
              <Chip
                key={s.value}
                label={s.label}
                icon={s.icon}
                color={severity === s.value ? s.color : 'default'}
                variant={severity === s.value ? 'filled' : 'outlined'}
                onClick={() => setSeverity(s.value)}
                clickable
                size="small"
              />
            ))}
          </Box>

          {/* Area */}
          <FormControl fullWidth size="small">
            <InputLabel id="report-area-label">Área</InputLabel>
            <Select
              labelId="report-area-label"
              value={area}
              onChange={e => setArea(e.target.value)}
              label="Área"
            >
              {AREAS.map(a => (
                <MenuItem key={a.value} value={a.value}>
                  {a.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Title */}
          <TextField
            fullWidth
            size="small"
            label="Título *"
            value={title}
            onChange={e => setTitle(e.target.value)}
            inputProps={{ maxLength: 200 }}
            helperText={`${title.length}/200`}
            required
          />

          {/* Description */}
          <TextField
            multiline
            rows={4}
            fullWidth
            size="small"
            label="Descripción (opcional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            helperText={
              <span style={{ color: isOverLimit ? 'red' : undefined }}>
                {`${charCount}/5000`}
              </span>
            }
            error={isOverLimit}
          />

          {/* Screenshot consent */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeScreenshot}
                  onChange={e => setIncludeScreenshot(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Incluir captura de pantalla
                </Typography>
              }
            />
            {includeScreenshot && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentGiven}
                    onChange={e => setConsentGiven(e.target.checked)}
                    size="small"
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Acepto que se adjunte la captura de pantalla actual y los últimos mensajes
                    del hilo (pueden contener datos personales)
                  </Typography>
                }
              />
            )}
            {screenshotBlob && includeScreenshot && consentGiven && (
              <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
                Captura pre-tomada lista para adjuntar.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={submitting || !title.trim() || isOverLimit || (includeScreenshot && !consentGiven)}
          startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
          data-testid="report-submit-btn"
        >
          {submitting ? 'Enviando…' : 'Enviar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;
